import { asCategoryId, getRootCategory } from '../category/category';
import { asEntityId } from '../entity/entity';
import { localize } from '../localized/localized';
import { ICategorized, ICategory, IEntity, IEquatable, ILocale, ILocalizable, IMarket, IMedia, IMemoryStore, IRoute } from '../types';
import { asEquatable } from '../utils';

/**
 * Represents the route strategy options.
 * - **Auto** dynamically add/remove prefixes based on number of markets & languages.
 * - **Default** add prefixes based on !isDefault market or language flag.
 * - **Manual** add prefixes based on !isHidden market or language flag.
 */
export enum RouteStrategy {
  Auto = 'auto',
  Default = 'default',
  Manual = 'manual',
}

const ROUTE_STRATEGY: RouteStrategy = (process.env.ROUTE_STRATEGY as RouteStrategy) || RouteStrategy.Manual;

function getPublicRedirectId(value: ICategorized['publicRedirect']): IEquatable | undefined {
  if (value && typeof value === 'object' && 'relationTo' in value && 'value' in value) {
    const relationValue = (value as { value: IEquatable | IEntity | IEquatable[] }).value;
    return Array.isArray(relationValue) ? undefined : asEntityId(relationValue);
  }
  return asEntityId(value);
}

/**
 * Collects routes based on the provided data.
 *
 * @param store - The memory store containing categorized items.
 * @param categories - The list of categories.
 * @param markets - The list of markets.
 * @param locales - The list of locales.
 * @returns An array of collected routes.
 */
export function collectRoutes(store: IMemoryStore<ICategorized>, categories: ICategory[], markets: IMarket[], locales: ILocale[], medias?: IMedia[]): IRoute[] {
  const routes: IRoute[] = [];
  const redirects = new Map<IEquatable, IEquatable>();
  if (locales.length > 0 && markets.length > 0) {
    const rootCategory = getRootCategory(categories);
    const activeLocales = locales.filter(locale => locale.isActive !== false);
    const activeMarkets = markets.filter(market => market.isActive !== false);
    const categoryById = new Map(categories.filter(category => typeof category.id === 'string').map(category => [category.id, category]));
    const localeById = new Map(activeLocales.filter(locale => typeof locale.id === 'string').map(locale => [locale.id, locale]));
    const mediaById = new Map((medias || []).filter(media => typeof media.id === 'string').map(media => [media.id, media]));
    const marketLocales = activeMarkets.flatMap(market => {
      const localesForMarket = market.languages && market.languages.length > 0 ?
        market.languages.map(language => localeById.get(String(asEntityId(language)))).filter(locale => locale !== undefined) as ILocale[] :
        activeLocales;
      return localesForMarket.map(locale => ({ market, locale }));
    });
    const getSegments = (item: ICategorized) => {
      const routeCategories: string[] = [];
      const segments: ICategory[] = [];
      let parentId: string | undefined = asCategoryId(item.category);
      while (parentId != null) {
        const parentCategory = categoryById.get(parentId);
        if (parentCategory) {
          segments.unshift({ ...parentCategory });
          routeCategories.push(parentId);
          parentId = asCategoryId(parentCategory.category);
          if (parentId && routeCategories.includes(parentId)) {
            console.log('ICategory.getParentCategoriesById', 'circular reference found', parentId);
            parentId = undefined;
          }
        } else {
          parentId = undefined;
        }
      }
      return segments;
    };
    // console.log('rootCategory', rootCategory);
    eachCategorized(store, (item, key) => {
      const segments = getSegments(item);
      const visibleSegments = segments.filter(x => x.id !== rootCategory?.id && Boolean(x.isHidden) === false);
      const visibleCategory = visibleSegments.length > 0 ? visibleSegments[visibleSegments.length - 1] : rootCategory;
      if (visibleCategory) {
        const slugs = visibleSegments.map(x => ({ slug: x.slug }));
        if (Boolean(item.isDefault) === false) {
          slugs.push({ slug: item.slug });
        }
        // const defaultLocale = locales.find(x => x.isDefault) || locales[0];
        // const defaultMarket = markets.find(x => x.isDefault) || markets[0];
        // const defaultMarketLocale = defaultMarket && defaultMarket.defaultLanguage ? asEntityId(defaultMarket.defaultLanguage) as string : defaultLocale.id;
        const isRootCategory = rootCategory &&
          item.category === rootCategory.id &&
          item.isDefault;
        // console.log('isRootCategory', isRootCategory, item.category);
        marketLocales.forEach(({ market, locale }) => {
          if (hasMarket(item, market)) {
            const title = localize(item.title, locale.id);
            const id = getRouteHref(slugs, market, locale, activeMarkets, activeLocales);
            const {
              _status,
              category,
              isDefault,
              media,
              order,
              isProtected,
              template,
              updatedAt,
              useSplat,
            } = item;
            const route: IRoute = {
              _status,
              category,
              id,
              locale: locale.id,
              market: market.id,
              media: routeMedia(media),
              page: item.id,
              resolvedCategory: visibleCategory.id,
              schema: key,
              template,
              title,
              updatedAt,
            } as IRoute;
            if (isDefault) {
              route.isDefault = true;
            }
            if (isProtected) {
              route.isProtected = true;
              const publicRedirect = getPublicRedirectId(item.publicRedirect);
              if (publicRedirect != null) {
                redirects.set(item.id, publicRedirect);
              }
            }
            if (isRootCategory) {
              route.isRoot = true;
            }
            const noindex = (item.meta?.robots || '').includes('noindex');
            if (noindex) {
              route.noindex = true;
            }
            if (order != null) {
              route.order = order;
            }
            if (useSplat) {
              route.useSplat = true;
            }
            const mediaId: string | undefined = typeof item.media === 'string' ? item.media : undefined;
            if (mediaId) {
              const media = mediaById.get(mediaId);
              if (media) {
                route.media = routeMedia(media);
              }
            }
            routes.push(route);
          }
        });
      }
    });
    const routeByPageContext = new Map<string, IRoute>();
    for (const route of routes) {
      routeByPageContext.set(JSON.stringify([route.page, route.market, route.locale]), route);
    }
    for (const route of routes) {
      const targetPage = redirects.get(route.page);
      if (targetPage != null) {
        const target = routeByPageContext.get(JSON.stringify([targetPage, route.market, route.locale]));
        if (target) {
          route.publicRedirect = target.id;
        }
      }
    }
  }
  return routes;
}

function routeMedia(media: IMedia | undefined): { url: string; type: string; updatedAt?: string | Date } | undefined {
  const url = media?.url || media?.src;
  return media && url ? {
    // createdAt: media.createdAt,
    updatedAt: media.updatedAt,
    // filename: media.filename,
    // mimeType: media.mimeType,
    // id: media.id,
    url: url,
    type: media.type,
  } : undefined;
}

/**
 * Iterates over each market and locale combination and invokes the callback function.
 *
 * @param markets - The list of markets.
 * @param locales - The list of locales.
 * @param callback - The callback function to be invoked for each market and locale combination.
 */
export function eachMarketLocale(markets: IMarket[], locales: ILocale[], callback: (market: IMarket, locale: ILocale, markets: IMarket[], locales: ILocale[]) => void) {
  locales = locales.filter(x => x.isActive !== false);
  markets = markets.filter(x => x.isActive !== false);
  markets.forEach(market => {
    const marketLocales: ILocale[] = (market.languages && market.languages.length > 0) ?
      market.languages.map(idOrEntity => locales.find(x => x.id === asEntityId(idOrEntity))).filter(x => x !== undefined) as ILocale[] :
      locales;
    marketLocales.forEach(locale => {
      callback(market, locale, markets, locales);
    });
  });
}

/**
 * Iterates over each categorized item in the store and invokes the callback function.
 *
 * @param store - The memory store containing categorized items.
 * @param callback - The callback function to be invoked for each categorized item.
 */
export function eachCategorized(store: IMemoryStore<ICategorized>, callback: (item: ICategorized, key: string) => void) {
  const keys = Object.keys(store);
  for (const key of keys) {
    const items = store[key]!;
    for (const item of items) {
      callback(item, key);
    }
  }
}

/**
 * Finds the default route of a category by categoryId, market, and locale.
 * @param routes - The array of routes to search.
 * @param categoryId - The category ID to match.
 * @param market - The market to match (optional).
 * @param locale - The locale to match (optional).
 * @returns The matching route, if found. Otherwise, undefined.
 */
export function findRouteOfCategory(routes: IRoute[], categoryId: IEquatable, market?: string, locale?: string): IRoute | undefined {
  const findRoute = (market && locale) ? (
    (route: IRoute) => route.isDefault &&
      route.resolvedCategory === categoryId &&
      route.market === market &&
      route.locale === locale
  ) : (
    (route: IRoute) => route.isDefault &&
      route.resolvedCategory === categoryId
  );
  const route = routes.find(findRoute);
  return route;
}

/**
 * Finds the routes of a category by categoryId.
 * @param routes - The array of routes to search.
 * @param categoryId - The category ID to filter routes by.
 * @param isDefault - Optional. Specifies whether to filter routes by default status. Default is true.
 * @returns An array of routes that match the specified category and default status.
 */
export function findRoutesOfCategory(routes: IRoute[], categoryId: IEquatable, isDefault: boolean = true): IRoute[] {
  const filteredRoutes = routes.filter((route: IRoute) =>
    route.resolvedCategory === categoryId &&
    Boolean(route.isDefault) === isDefault
  );
  return filteredRoutes;
}

/**
 * Generates the route href based on the provided segments, market, locale, markets, and locales.
 *
 * @param segments - The segments of the route.
 * @param market - The market object.
 * @param locale - The locale object.
 * @param markets - The list of markets.
 * @param locales - The list of locales.
 * @returns The generated route href.
 */
export function getRouteHref(segments: { slug: ILocalizable }[], market: IMarket, locale: ILocale, markets: IMarket[], locales: ILocale[]): string {
  const href = segments.reduce((p, c, i) => {
    let slug = localize(c.slug || '', locale.id);
    slug = `${p}/${slug}`;
    return slug === '/' ? '' : slug;
  }, '');
  const prefixes: string[] = [];
  switch (ROUTE_STRATEGY) {
    case RouteStrategy.Auto:
      if (locales.length > 1 || markets.length > 1) {
        prefixes.push(locale.id);
      }
      if (markets.length > 1) {
        prefixes.push(market.id);
      }
      break;
    case RouteStrategy.Default:
      if (locale.isDefault !== true) {
        prefixes.push(locale.id);
      }
      if (market.isDefault !== true) {
        prefixes.push(market.id);
      }
      break;
    case RouteStrategy.Manual:
      if (locale.isHidden !== true) {
        prefixes.push(locale.id);
      }
      if (market.isHidden !== true) {
        prefixes.push(market.id);
      }
      break;
  }
  const prefix = prefixes.length ? `/${prefixes.join('-')}` : '';
  return `${prefix}${href}`;
}

// import { isNonEmptyArray } from '../utils/typeGuards';

export function hasMarket(item: ICategorized, market: IMarket): boolean {
  // return isNonEmptyArray(item.markets) ? item.markets.includes(market.id) : true;
  return !item.markets ||
    item.markets.length === 0 ||
    item.markets.map(x => asEquatable(x)).includes(market.id);
}
